/**
 * Rate Propagation Store
 * Tracks the state of rate change propagation.
 * When an admin action triggers propagateRateChanges(), this store
 * holds progress/result state so the UI can show a banner.
 */

import { create } from 'zustand'

interface FailedEntry {
  entryId: string
  memberName: string
  projectName: string
  reason: string
  date: string
}

export type StatusFilter = ('DRAFT' | 'SUBMITTED' | 'CHANGES_REQUESTED' | 'REJECTED')[]

export type PropagationContext = {
  triggeredBy: string
  memberId?: string
  rateCardId?: string
  projectId?: string
  entityId?: string
  roleId?: string
}

interface RatePropagationState {
  // Propagation banner state
  status: 'idle' | 'running' | 'complete'
  total: number
  updated: number
  skipped: number
  failed: FailedEntry[]
  dismissed: boolean
  retryRequested: boolean
  lastContext: PropagationContext | null

  // Impact dialog state
  impactDialogOpen: boolean
  pendingAction: ((statusFilter: StatusFilter) => Promise<void>) | null
  impactCounts: { draftCount: number; submittedCount: number } | null

  // Propagation banner actions
  startPropagation: (total?: number) => void
  setContext: (context: PropagationContext | null) => void
  completePropagation: (result: {
    updated: number
    skipped: number
    failed: FailedEntry[]
  }) => void
  dismiss: () => void
  requestRetry: () => void
  reset: () => void

  // Impact dialog actions
  openImpactDialog: (counts: { draftCount: number; submittedCount: number }, pendingAction: (statusFilter: StatusFilter) => Promise<void>) => void
  closeImpactDialog: () => void
}

export const useRatePropagationStore = create<RatePropagationState>(
  (set) => ({
    // Initial state
    status: 'idle',
    total: 0,
    updated: 0,
    skipped: 0,
    failed: [],
    dismissed: false,
    retryRequested: false,
    lastContext: null,
    impactDialogOpen: false,
    pendingAction: null,
    impactCounts: null,

    // Actions
    startPropagation: (total = 0) =>
      set({
        status: 'running',
        total,
        updated: 0,
        skipped: 0,
        failed: [],
        dismissed: false,
        retryRequested: false,
      }),

    setContext: (context) => set({ lastContext: context }),

    completePropagation: (result) =>
      set({
        status: 'complete',
        updated: result.updated,
        skipped: result.skipped,
        failed: result.failed,
      }),

    dismiss: () => set({ dismissed: true, status: 'idle', lastContext: null }),

    requestRetry: () =>
      set((state) => ({
        status: 'running',
        total: state.failed.length,
        updated: 0,
        skipped: 0,
        failed: [],
        retryRequested: true,
      })),

    reset: () =>
      set({
        status: 'idle',
        total: 0,
        updated: 0,
        skipped: 0,
        failed: [],
        dismissed: false,
        retryRequested: false,
        lastContext: null,
      }),

    openImpactDialog: (counts, pendingAction) =>
      set({
        impactDialogOpen: true,
        pendingAction,
        impactCounts: counts,
      }),

    closeImpactDialog: () =>
      set({
        impactDialogOpen: false,
        pendingAction: null,
        impactCounts: null,
      }),
  }),
)
